import { Dark } from "quasar"
import { computed, reactive, type ComputedRef } from "vue"

/**
 * Light/dark appearance. Three modes, mirroring what every OS/browser offers:
 * `auto` follows the system's `prefers-color-scheme` (and keeps following it while the app is open),
 * `light`/`dark` pin one. The choice is **device-local** (`localStorage`) rather than a server-side
 * user setting — it belongs to the machine/browser you're sitting at, exactly like the OS setting it
 * defaults to, so the same account can be light on a desktop and dark on a phone. That also keeps
 * this a pure frontend feature: no `shared` API change, and it works identically in mock mode.
 *
 * All the actual theming is Quasar's `Dark` plugin (it toggles `body--dark` / `body--light`, which
 * every Quasar component styles itself against); our own colours hang off the same body class — see
 * `css/theme.scss`.
 */
export type ThemeMode = "auto" | "light" | "dark"

/** localStorage key holding the selected [ThemeMode]. An absent/invalid value means `auto`. */
export const THEME_MODE_KEY = "bx-ops-theme"

const THEME_MODES: readonly ThemeMode[] = ["auto", "light", "dark"]

export interface ThemeStore {
  /** The selected mode (what the switcher shows). */
  readonly mode: ThemeMode
  /** Whether dark is *effectively* active right now — under `auto` this follows the system. */
  readonly isDark: boolean
  /** Select a mode: applies it immediately and persists it for the next load. */
  setMode(mode: ThemeMode): void
}

const state = reactive<{ mode: ThemeMode; loginScreen: boolean }>({
  mode: "auto",
  loginScreen: false,
})

function apply(): void {
  if (state.loginScreen) {
    Dark.set(false)
    return
  }
  // "auto" hands the decision to Quasar's prefers-color-scheme watcher; the others pin a value.
  Dark.set(state.mode === "auto" ? "auto" : state.mode === "dark")
}

// `Dark.isActive` is reactive, so under `auto` this flips with the system setting on the fly.
const isDark: ComputedRef<boolean> = computed(() => Dark.isActive)

const store: ThemeStore = {
  get mode(): ThemeMode {
    return state.mode
  },
  get isDark(): boolean {
    return isDark.value
  },
  setMode(mode: ThemeMode): void {
    state.mode = mode
    apply()
    try {
      localStorage.setItem(THEME_MODE_KEY, mode)
    } catch {
      // private mode / storage full: the theme still applies for this session
    }
  },
}

// Module-level reactive singleton (same pattern as the other stores — no Pinia in this app).
export function useThemeStore(): ThemeStore {
  return store
}

/**
 * Pins LIGHT for the public login flow, whatever the preference says: it leads to the IdP's own login
 * page, which is light and not ours to theme, so a dark ops login put a bright page between two dark
 * ones. The stored mode is untouched. Not used for the session-lock overlay — that sits on top of the
 * running app, where a light dialog would read as broken.
 */
export function setLoginScreen(active: boolean): void {
  state.loginScreen = active
  apply()
}

/**
 * Applies the stored mode. Called from the `theme` boot file, i.e. **before the app mounts**, so the
 * first paint is already in the right theme (no light-mode flash).
 */
export function initTheme(): void {
  let stored: string | null = null
  try {
    stored = localStorage.getItem(THEME_MODE_KEY)
  } catch {
    // no storage access — fall back to auto
  }
  store.setMode(isThemeMode(stored) ? stored : "auto")
}

function isThemeMode(value: string | null): value is ThemeMode {
  return value !== null && (THEME_MODES as readonly string[]).includes(value)
}
